/**
 * 環境感知的 Logger 系統
 *
 * 功能：
 * - 生產環境自動隱藏 console 輸出，避免洩露敏感資訊
 * - 開發環境保留完整的 debug 資訊
 * - 支援不同的日誌等級
 * - 可選地將錯誤發送到錯誤追蹤服務（如 Sentry）
 */

import { ENV } from '@/config';

/**
 * 日誌等級
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

/**
 * Logger 配置
 */
interface LoggerConfig {
  /** 最小日誌等級 */
  minLevel: LogLevel;
  /** 是否啟用 console 輸出 */
  enableConsole: boolean;
  /** 是否發送到錯誤追蹤服務 */
  enableErrorTracking: boolean;
  /** 日誌前綴 */
  prefix?: string;
}

/**
 * 判斷是否為生產環境
 */
function isProduction(): boolean {
  return import.meta.env.PROD || import.meta.env.VITE_ENV === 'production';
}

/**
 * 判斷是否為本地開發環境
 */
function isLocal(): boolean {
  return (
    import.meta.env.DEV ||
    import.meta.env.VITE_ENV === 'development' ||
    ENV.IS_LOCAL
  );
}

/**
 * 預設配置
 */
const defaultConfig: LoggerConfig = {
  minLevel: isProduction() ? LogLevel.WARN : LogLevel.DEBUG,
  enableConsole: isLocal(),
  enableErrorTracking: isProduction(),
  prefix: '[RaisePlan]',
};

/**
 * Logger 類別
 */
class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
  }

  /**
   * 格式化訊息
   */
  private formatMessage(level: string, ...args: any[]): any[] {
    const timestamp = new Date().toISOString();
    const prefix = this.config.prefix ? `${this.config.prefix} ` : '';
    return [`${prefix}[${level}] ${timestamp}`, ...args];
  }

  /**
   * 檢查是否應該輸出此等級的日誌
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.minLevel;
  }

  /**
   * Debug 等級日誌
   * 用於詳細的除錯資訊
   */
  debug(...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG) && this.config.enableConsole) {
      console.debug(...this.formatMessage('DEBUG', ...args));
    }
  }

  /**
   * Info 等級日誌
   * 用於一般資訊
   */
  info(...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO) && this.config.enableConsole) {
      console.info(...this.formatMessage('INFO', ...args));
    }
  }

  /**
   * Warning 等級日誌
   * 用於警告訊息
   */
  warn(...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN) && this.config.enableConsole) {
      console.warn(...this.formatMessage('WARN', ...args));
    }
  }

  /**
   * Error 等級日誌
   * 用於錯誤訊息，並可選地發送到錯誤追蹤服務
   */
  error(...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      if (this.config.enableConsole) {
        console.error(...this.formatMessage('ERROR', ...args));
      }

      // 在生產環境中發送到錯誤追蹤服務
      if (this.config.enableErrorTracking) {
        this.sendToErrorTracking(args);
      }
    }
  }

  /**
   * 發送錯誤到追蹤服務
   * 預留整合 Sentry 等服務的介面
   */
  private sendToErrorTracking(args: any[]): void {
    // TODO: 整合 Sentry 或其他錯誤追蹤服務
    // Example:
    // if (window.Sentry) {
    //   window.Sentry.captureException(args[0]);
    // }

    // 目前只在 console 中標記
    if (isLocal()) {
      console.warn('[ErrorTracking] Would send to error tracking service:', args);
    }
  }

  /**
   * 群組日誌開始
   */
  group(label: string): void {
    if (this.config.enableConsole) {
      console.group(label);
    }
  }

  /**
   * 群組日誌結束
   */
  groupEnd(): void {
    if (this.config.enableConsole) {
      console.groupEnd();
    }
  }

  /**
   * 計時器開始
   */
  time(label: string): void {
    if (this.config.enableConsole) {
      console.time(label);
    }
  }

  /**
   * 計時器結束
   */
  timeEnd(label: string): void {
    if (this.config.enableConsole) {
      console.timeEnd(label);
    }
  }

  /**
   * 表格輸出
   */
  table(data: any): void {
    if (this.config.enableConsole) {
      console.table(data);
    }
  }

  /**
   * 更新配置
   */
  setConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 取得當前配置
   */
  getConfig(): Readonly<LoggerConfig> {
    return { ...this.config };
  }
}

/**
 * 全域 Logger 實例
 */
export const logger = new Logger();

/**
 * 建立具名 Logger
 * 用於不同模組或功能區域
 *
 * @param name - Logger 名稱
 * @returns Logger 實例
 *
 * @example
 * ```typescript
 * const authLogger = createLogger('Auth');
 * authLogger.info('User logged in');
 * ```
 */
export function createLogger(name: string): Logger {
  return new Logger({ prefix: `[${name}]` });
}

// 在生產環境中禁用原生 console（可選）
if (isProduction()) {
  // 保留 error，其他都禁用
  const noop = () => {};
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.warn = noop;
  // console.error 保留用於緊急情況
}
